import { Analytics } from '@util/Analytics';
import React, { ReactElement } from 'react';
import { ScreenType, SelectScreenConfig, SharedScreenName } from '../../config/FlowTypes';
// Use of the import * as ... pattern allows mocking using
// const dispatchUserDataSpy = jest.spyOn(UserDataActions, 'dispatchUserData');
// in SingleSelectScreen.test.tsx
import * as UserDataActions from '../../store/userData/UserDataActions';
import { ScreenWrapper } from '../styles/Containers';
import { FlowRoute, NavigationProps } from './FlowNavigation';
import { SELECT_DELAY_DEFAULT_MS } from './SingleSelectDefaultDelay';
import { SelectLayoutConfig, SingleSelectLayout } from './layouts/SingleSelectLayout';

export type SingleSelectScreenConfig<ScreenNames extends string> = SelectScreenConfig<
  ScreenType.SingleSelect,
  SelectLayoutConfig,
  ScreenNames
> & { delayInMsBetweenClickAndNavigate?: number };

type Props<ScreenNames extends string> = NavigationProps<
  SingleSelectScreenConfig<ScreenNames>,
  ScreenNames
>;

export const SingleSelectScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const delayInMsBetweenClickAndNavigate =
    props.delayInMsBetweenClickAndNavigate ?? SELECT_DELAY_DEFAULT_MS;
  function onSelected(selected: string) {
    // Small action timeout so user can actually see what option was clicked
    setTimeout(() => onSingleSelectSubmit({ props, selected }), delayInMsBetweenClickAndNavigate);
  }

  return (
    <ScreenWrapper>
      <SingleSelectLayout
        onSelected={onSelected}
        {...props.layout}
        onShowToast={props.onShowToast}
      />
    </ScreenWrapper>
  );
};

type SingleSelectSubmitArgs<ScreenNames extends string> = {
  props: NavigationProps<FlowRoute<ScreenNames>, ScreenNames>;
  selected: string;
  isPHI?: boolean;
};

function recordHcpReferredEvent(doctorAnswer: string) {
  if (doctorAnswer === 'Yes') {
    Analytics.track({ event: Analytics.Event.h_hcp_referred });
  }
}

export function onSingleSelectSubmit<ScreenNames extends string>(
  args: SingleSelectSubmitArgs<ScreenNames>
): void {
  const { props, selected, isPHI = false } = args;

  UserDataActions.dispatchUserData({ [props.screenName]: [selected] });
  Analytics.track({
    event: Analytics.Event.o_question_answered,
    question: props.screenName,
    ...(isPHI ? {} : { answer: selected }),
  });
  if (props.screenName === SharedScreenName.Doctor) {
    recordHcpReferredEvent(selected);
  }

  props.navigation.next(props);
}
